/**
 * Theme token registry — GENERATED FROM theme.css.
 *
 * theme.css is the source of truth: it is what paints before React
 * mounts, and it is the cascade floor beneath the inline custom
 * properties ThemeProvider writes. This file is the same data in the
 * shape the provider needs.
 *
 * DO NOT EDIT BY HAND. Edit theme.css, then regenerate. `theme.test.ts`
 * fails if the two drift, because a value that differs between them
 * changes colour the instant React mounts — a flicker that cannot be
 * found by reading either file alone.
 *
 * Both maps define EVERY token. They did not always: darkTheme had 29
 * of the 67, and because ThemeProvider clears each key before applying
 * the next set, the other 38 were genuinely undefined in dark mode.
 */
export type ThemeName = "light" | "dark";

export type ThemeVars = Record<string, string>;

export const lightTheme: ThemeVars = {
  "--background": "240 240 238",
  "--background-card": "255 255 255",
  "--background-subtle": "247 247 246",
  "--background-nav": "247 247 247",
  "--background-topbar": "247 247 247",
  "--nav-text": "111 111 106",
  "--nav-text-active": "47 125 42",
  "--nav-hover": "235 235 232",
  "--foreground": "43 43 43",
  "--foreground-muted": "120 120 116",
  "--label-text": "96 96 93",
  "--answer-text": "32 32 32",
  "--foreground-body": "74 74 72",
  "--foreground-heading": "31 31 30",
  "--foreground-on-chrome": "43 43 43",
  "--foreground-link": "47 125 42",
  "--border": "226 226 222",
  "--border-strong": "204 203 198",
  "--border-accent": "142 198 63",
  "--accent": "65 171 56",
  "--accent-hover": "47 125 42",
  "--accent-bright": "65 171 56",
  "--accent-light": "237 246 230",
  "--accent-text": "255 255 255",
  "--accent-foreground": "255 255 255",
  "--status-new": "43 108 163",
  "--status-new-bg": "233 241 247",
  "--sitting-ground": "238 240 242",
  "--sitting-note-bg": "234 241 254",
  "--sitting-note-border": "201 219 250",
  "--status-wip": "176 115 14",
  "--status-wip-strong": "138 90 9",
  "--status-wip-border": "224 196 138",
  "--status-wip-bg": "251 241 222",
  "--status-done": "47 125 42",
  "--status-done-bg": "237 246 230",
  "--status-urgent": "194 58 48",
  "--status-urgent-bg": "251 235 233",
  "--table-header-bg": "240 240 238",
  "--table-header-text": "43 43 43",
  "--table-row-bg": "255 255 255",
  "--table-row-alt": "250 250 249",
  "--table-row-hover": "242 242 239",
  "--table-border": "226 226 222",
  "--input-bg": "255 255 255",
  "--input-border": "204 203 198",
  "--input-border-focus": "65 171 56",
  "--input-text": "43 43 43",
  "--input-placeholder": "117 117 117",
  "--input-disabled-bg": "240 240 238",
  "--badge-sonographer": "107 70 193",
  "--badge-sonographer-bg": "239 233 251",
  "--badge-radiologist": "47 125 42",
  "--badge-radiologist-bg": "237 246 230",
  "--badge-admin": "43 43 43",
  "--badge-admin-bg": "236 236 234",
  "--badge-reception": "111 111 106",
  "--badge-reception-bg": "244 244 242",
  "--badge-dicom": "107 70 193",
  "--badge-dicom-bg": "239 233 251",
  "--avatar-purple": "107 70 193",
  "--avatar-teal": "47 143 134",
  "--avatar-coral": "194 58 48",
  "--avatar-blue": "43 108 163",
  "--avatar-green": "65 171 56",
  "--avatar-text": "255 255 255",
  "--avatar-sono-bg": "107 70 193",
  "--avatar-sono-text": "255 255 255",
  "--avatar-radio-bg": "43 108 163",
  "--avatar-radio-text": "255 255 255",
  "--user-badge-bg": "237 246 230",
  "--user-badge-text": "47 125 42",
  "--stat-card-bg": "255 255 255",
  "--stat-card-border": "226 226 222",
  "--shadow-sm": "0 1px 2px rgba(18, 26, 32, 0.05)",
  "--shadow-md": "0 1px 2px rgba(18, 26, 32, 0.05), 0 8px 24px -12px rgba(18, 26, 32, 0.18)",
  "--shadow-lg": "0 2px 4px rgba(18, 26, 32, 0.06), 0 16px 40px -16px rgba(18, 26, 32, 0.24)",
};

export const darkTheme: ThemeVars = {
  "--background": "26 27 25",
  "--background-card": "35 36 32",
  "--background-subtle": "38 39 31",
  "--background-nav": "31 32 29",
  "--background-topbar": "31 32 29",
  "--nav-text": "168 168 161",
  "--nav-text-active": "127 208 116",
  "--nav-hover": "43 44 40",
  "--foreground": "236 234 229",
  "--foreground-muted": "163 163 155",
  "--label-text": "181 181 174",
  "--answer-text": "241 239 236",
  "--foreground-body": "214 212 205",
  "--foreground-heading": "245 243 238",
  "--foreground-on-chrome": "236 234 229",
  "--foreground-link": "127 208 116",
  "--border": "52 53 47",
  "--border-strong": "69 70 63",
  "--border-accent": "92 194 80",
  "--accent": "92 194 80",
  "--accent-hover": "127 208 116",
  "--accent-bright": "92 194 80",
  "--accent-light": "31 43 29",
  "--accent-text": "17 33 15",
  "--accent-foreground": "17 33 15",
  "--status-new": "106 169 216",
  "--status-new-bg": "22 35 44",
  "--sitting-ground": "20 21 19",
  "--sitting-note-bg": "24 33 48",
  "--sitting-note-border": "44 62 92",
  "--status-wip": "217 154 61",
  "--status-wip-strong": "233 176 98",
  "--status-wip-border": "92 70 32",
  "--status-wip-bg": "43 33 20",
  "--status-done": "111 194 100",
  "--status-done-bg": "23 36 26",
  "--status-urgent": "226 104 93",
  "--status-urgent-bg": "44 25 23",
  "--table-header-bg": "31 32 29",
  "--table-header-text": "236 234 229",
  "--table-row-bg": "35 36 32",
  "--table-row-alt": "38 39 31",
  "--table-row-hover": "44 45 39",
  "--table-border": "52 53 47",
  "--input-bg": "28 29 26",
  "--input-border": "69 70 63",
  "--input-border-focus": "92 194 80",
  "--input-text": "236 234 229",
  "--input-placeholder": "125 125 117",
  "--input-disabled-bg": "35 36 32",
  "--badge-sonographer": "185 163 238",
  "--badge-sonographer-bg": "36 31 51",
  "--badge-radiologist": "127 208 116",
  "--badge-radiologist-bg": "23 36 26",
  "--badge-admin": "236 234 229",
  "--badge-admin-bg": "43 44 40",
  "--badge-reception": "163 163 155",
  "--badge-reception-bg": "38 39 31",
  "--badge-dicom": "185 163 238",
  "--badge-dicom-bg": "36 31 51",
  "--avatar-purple": "139 106 216",
  "--avatar-teal": "63 167 157",
  "--avatar-coral": "226 104 93",
  "--avatar-blue": "74 144 199",
  "--avatar-green": "92 194 80",
  "--avatar-text": "17 33 15",
  "--avatar-sono-bg": "139 106 216",
  "--avatar-sono-text": "255 255 255",
  "--avatar-radio-bg": "74 144 199",
  "--avatar-radio-text": "255 255 255",
  "--user-badge-bg": "31 43 29",
  "--user-badge-text": "127 208 116",
  "--stat-card-bg": "35 36 32",
  "--stat-card-border": "52 53 47",
  "--shadow-sm": "0 1px 2px rgba(0, 0, 0, 0.4)",
  "--shadow-md": "0 1px 2px rgba(0, 0, 0, 0.4), 0 8px 24px -12px rgba(0, 0, 0, 0.6)",
  "--shadow-lg": "0 2px 4px rgba(0, 0, 0, 0.5), 0 16px 40px -16px rgba(0, 0, 0, 0.7)",
};

export const themes: Record<ThemeName, ThemeVars> = {
  light: lightTheme,
  dark: darkTheme,
};
